import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';

export interface DrawerProps {
    open?: boolean;
    title?: ReactNode;
    subtitle?: ReactNode;
    width?: number | string;
    side?: 'right' | 'left';
    /** Conteúdo extra no cabeçalho, antes do botão de fechar. */
    header?: ReactNode;
    footer?: ReactNode;
    children?: ReactNode;
    onClose?: () => void;
    style?: CSSProperties;
}

/**
 * Painel lateral de **consulta contextual**, onde ver a tela atrás faz parte da
 * tarefa. Hoje só o assistente o usa, e isso é exceção intencional ao padrão de
 * modais — decidida e registrada. Para criar, editar, ver detalhe ou confirmar,
 * use `Modal`.
 */
export function Drawer({
    open = false, title, subtitle, width = 420, side = 'right', header, footer, children, onClose, style,
}: DrawerProps) {
    if (!open) return null;
    return (
        /* Fecha só quando o clique foi no próprio fundo: comparar alvo com o
           elemento que ouve dispensa `stopPropagation` no painel. */
        <div
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            style={{ position: 'fixed', inset: 0, zIndex: 55, background: 'rgba(17,19,24,.28)', display: 'flex', justifyContent: side === 'right' ? 'flex-end' : 'flex-start' }}
        >
            <aside
                aria-label={typeof title === 'string' ? title : undefined}
                style={{
                    width, maxWidth: '100%', height: '100%', background: 'var(--surface)', display: 'flex', flexDirection: 'column',
                    boxShadow: 'var(--shadow-xl)', animation: `vec-slide-${side} var(--dur-slow) var(--ease-entrance)`, ...style,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px', height: 56, borderBottom: '1px solid var(--border-subtle)', flex: '0 0 auto' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: 'var(--text-primary)' }}>{title}</p>
                        {subtitle && <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>{subtitle}</p>}
                    </div>
                    {header}
                    <button
                        type="button" onClick={onClose}
                        aria-label={typeof title === 'string' ? `Fechar ${title}` : 'Fechar painel'}
                        style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--neutral-500)' }}
                    >
                        <Icon name="x" size={17} />
                    </button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>{children}</div>
                {footer && (
                    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: 14, display: 'flex', justifyContent: 'flex-end', gap: 8, flex: '0 0 auto' }}>{footer}</div>
                )}
                <style>{'@keyframes vec-slide-right{from{transform:translateX(24px);opacity:.6}to{transform:none;opacity:1}}@keyframes vec-slide-left{from{transform:translateX(-24px);opacity:.6}to{transform:none;opacity:1}}'}</style>
            </aside>
        </div>
    );
}
